import {
  Zap, Activity, Users, Medal, Droplets,
} from 'lucide-react';

export const COMMON_INPUT_CLASS =
  'w-full border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900 text-slate-800 dark:text-white placeholder:text-slate-400 p-3 rounded-xl mt-1 outline-none transition-[border-color,box-shadow,background-color] duration-200 hover:border-slate-300 dark:hover:border-slate-600 focus:bg-white dark:focus:bg-slate-900 focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10';

/** Katman sırası — 2 adımlı onay, normal modalın üstünde */
export const Z_INDEX = {
  header: 20,
  sidebarOverlay: 30,
  sidebar: 40,
  cookieBanner: 45,
  modal: 50,
  notificationMenu: 55,
  confirmModal: 58,
  survey: 60,
  toast: 70,
};

/** Haftanın günleri — Date.getDay() indeksi (0 = Pazar) */
export const DAYS_TR = [
  'Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi',
];

export const USER_ROLE = {
  STUDENT: 'student',
  INSTRUCTOR: 'instructor',
};

export const COLLECTIONS = {
  STUDENTS: 'students',
  INSTRUCTORS: 'instructors',
  REQUESTS: 'requests',
  NOTIFICATIONS: 'notifications',
  CONSENTS: 'consents',
  PAYMENTS: 'payments',
  /** E-posta / telefon tekilliği — belge kimliği normalize edilmiş değer */
  IDENTITIES: 'identities',
  /** Öğrenci ön kaydı (eğitmen onayı bekler) — belge kimliği normalize e-posta */
  STUDENT_SIGNUPS: 'studentSignups',
};

export const STORAGE_KEYS = {
  THEME: 'finly_theme',
  COOKIE_CONSENT: 'finly_cookie_consent',
  INTENDED_ROLE: 'intendedRole',
  AUTH_INTENT: 'finly_auth_intent',
  LAST_ROLE: 'finly_last_role',
  AUTO_LOGIN_DONE: 'finly_auto_login_done',
  /** Geri tuşu ile login'e dönüldüğünde ileri tuşu oturumu geri yükleyemesin */
  HISTORY_SESSION_REVOKED: 'finly_history_session_revoked',
  /** Şifreyle giriş sonrası verilen portal erişim jetonu (uid) */
  PORTAL_SESSION_UID: 'finly_portal_session_uid',
  /** Login formu gönderildi, profil bekleniyor */
  EXPLICIT_LOGIN_INTENT: 'finly_explicit_login_intent',
  /** Şifresiz e-posta OTP girişi için bekleyen adres */
  EMAIL_SIGN_IN: 'finly_email_sign_in',
  /** Kodun gönderildiği an (ms) — sayfa yeniden yüklenirse kod adımı geri açılır */
  EMAIL_SIGN_IN_AT: 'finly_email_sign_in_at',
  /** Eğitmen kaydı: e-posta/SMS doğrulama sonrası profil oluşturmak için */
  PENDING_INSTRUCTOR_REG: 'finly_pending_instructor_reg',
  /** Tarayıcıdan / sekmeden ayrılma zamanı (ms) — geri dönüşte 6 saat kuralı */
  AUTH_AWAY_SINCE: 'finly_auth_away_since',
};

/**
 * Uygulama kullanımdayken oturum bitmez.
 * Tarayıcıdan / sekmeden ayrılıp AUTH_AWAY_MAX_MS içinde dönülmezse tekrar giriş istenir.
 */
export const AUTH_AWAY_MAX_MS = 6 * 60 * 60 * 1000;

export const AUTH_AWAY_EXPIRED_MESSAGE =
  '6 saatten uzun süre uzak kaldığınız için oturum sonlandı. Tekrar giriş yapın.';

/** Landing → auth ekranı yönlendirme niyeti */
export const AUTH_INTENT = {
  LOGIN: 'login',
  REGISTER: 'register',
};

/**
 * Eğitmen hesabını yönetici açar (scripts/add-instructor.cjs).
 * Öğrenci kendisi ön kayıt olur; hesap sistem sahibi eğitmenin onayıyla açılır (requestStudentSignup).
 */
export const INSTRUCTOR_REGISTRATION_ENABLED = false;
export const STUDENT_REGISTRATION_ENABLED = true;
export const STUDENT_SIGNUP_PENDING_MESSAGE =
  'Kaydınız eğitmen onayı bekliyor. Onaylandığında e-posta ile bilgilendirileceksiniz.';
/** Bu yaşın altındaki öğrencide veli adı ve veli onayı zorunlu */
export const ADULT_AGE = 18;
/** Geriye dönük uyumluluk — genel bayrak eğitmen kaydına bağlı */
export const PUBLIC_REGISTRATION_ENABLED = INSTRUCTOR_REGISTRATION_ENABLED;
export const STUDENT_REGISTRATION_CLOSED_MESSAGE =
  'Öğrenci kaydı kapalı. Hesabınızı eğitmeniniz oluşturur; siz yalnızca giriş yapabilirsiniz.';
export const STUDENT_EMAIL_ONLY_MESSAGE =
  'Öğrenciler e-posta adresiyle giriş yapar. Kayıtlı e-posta adresinizi girin.';
export const REGISTRATION_CLOSED_MESSAGE = 'Yeni kayıtlar şu anda alınmıyor.';
export const INSTRUCTOR_REGISTRATION_CLOSED_HINT =
  'Eğitmen hesapları yönetici tarafından açılır.';

/** Sistem sahibi / KVKK veri sorumlusu (landing, KVKK metni, e-posta yanıt adresi) */
export const OWNER_NAME = 'Emre Aslan';
export const OWNER_EMAIL = 'emreaslanyuzme@gmail.com';
export const OWNER_PHONE_DISPLAY = '0542 891 8458';
export const OWNER_PHONE_E164 = '+905428918458';
/** Gmail SMTP adresi kimlik doğrulanan hesap olmalı; görünen ad "Atletik Yüzme" */
export const MAIL_SENDER = {
  from: 'Atletik Yüzme <eriendnmz@gmail.com>',
  replyTo: OWNER_EMAIL,
};

export const APP_PAGES = {
  LANDING: 'landing',
  LOGIN: 'login',
  KVKK: 'kvkk',
  STUDENT: 'student',
  INSTRUCTOR: 'instructor',
};

const navigateToAuthScreen = (navigation) => {
  if (typeof navigation?.goToLogin === 'function') {
    navigation.goToLogin();
  } else if (typeof navigation?.setCurrentPage === 'function') {
    navigation.setCurrentPage(APP_PAGES.LOGIN);
  }
};

/** Landing CTA → giriş ekranı (rol ön-seçimi opsiyonel; varsayılan eğitmen) */
export const goToLoginWithRole = (navigation, role = USER_ROLE.INSTRUCTOR) => {
  window.localStorage.setItem(STORAGE_KEYS.INTENDED_ROLE, role);
  window.localStorage.setItem(STORAGE_KEYS.AUTH_INTENT, AUTH_INTENT.LOGIN);
  navigateToAuthScreen(navigation);
};

/** Landing CTA → kayıt ekranı; öğrenci kaydı kapalıysa girişe yönlendirir */
export const goToRegisterWithRole = (navigation, role = USER_ROLE.INSTRUCTOR) => {
  if (role === USER_ROLE.STUDENT && !STUDENT_REGISTRATION_ENABLED) {
    goToLoginWithRole(navigation, USER_ROLE.STUDENT);
    return;
  }
  if (role === USER_ROLE.INSTRUCTOR && !INSTRUCTOR_REGISTRATION_ENABLED) {
    goToLoginWithRole(navigation, USER_ROLE.INSTRUCTOR);
    return;
  }
  window.localStorage.setItem(STORAGE_KEYS.INTENDED_ROLE, role);
  window.localStorage.setItem(STORAGE_KEYS.AUTH_INTENT, AUTH_INTENT.REGISTER);
  navigateToAuthScreen(navigation);
};

/** Öğrenci hesap durumu */
export const STUDENT_STATUS = {
  NEW: 'Yeni',
  ACTIVE: 'Aktif',
  COMPLETED: 'Tamamlandı',
  SUSPENDED: 'Askıda',
};

/** Aylık aidat/ödeme durumu — kayıt yoksa UNPAID kabul edilir */
export const PAYMENT_STATUS = {
  PAID: 'paid',
  UNPAID: 'unpaid',
};

export const PAYMENT_STATUS_LABEL = {
  [PAYMENT_STATUS.PAID]: 'Ödendi',
  [PAYMENT_STATUS.UNPAID]: 'Ödemedi',
};

/** Ödeme notu / tutar sınırları — Firestore rules ile birebir */
export const PAYMENT_LIMITS = {
  MAX_AMOUNT: 1000000,
  MAX_NOTE_LENGTH: 300,
};

/** Eğitmen hesap durumu (Firestore sorgularında kullanılan değerler) */
export const INSTRUCTOR_STATUS = {
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
};

export const WORKOUT_TEMPLATES = [
  {
    name: 'Sprint',
    icon: Zap,
    warmup: '400m Serbest Isınma + 4x50m Ayak',
    main: '8x50m Tam Güç Sprint (1 dk dinlenme) + 4x25m Sualtı',
    cooldown: '200m Çok Yavaş Serbest',
  },
  {
    name: 'Dayanıklılık',
    icon: Activity,
    warmup: '600m Karışık Isınma',
    main: '3x400m Serbest (%70 Tempo) + 2x200m Sırtüstü',
    cooldown: '300m Serbest Gevşeme',
  },
  {
    name: 'Çocuk Grubu',
    icon: Users,
    warmup: '200m Serbest + Oyunlu Nefes Egzersizi',
    main: '4x25m Tahtalı Ayak Vuruşu + 4x25m Serbest Kol',
    cooldown: '100m Sırtüstü Yavaş',
  },
  {
    name: 'Yarış Hazırlık',
    icon: Medal,
    warmup: '800m Isınma (Her 100m hızlanarak)',
    main: '4x100m Yarış Temposu + Çıkış/Dönüş Çalışması',
    cooldown: '400m Serbest Soğuma',
  },
  {
    name: 'Teknik Gelişim',
    icon: Droplets,
    warmup: '300m Serbest Isınma',
    main: '8x50m Tek Kol Serbest + 8x50m Yumruk Sıkılı Serbest',
    cooldown: '200m Sırtüstü Teknik',
  },
];

export const SWIMMING_STYLES = [
  '50m Serbest', '100m Serbest', '200m Serbest',
  '50m Kelebek', '100m Kelebek',
  '50m Kurbağalama', '100m Kurbağalama',
  '50m Sırtüstü', '100m Sırtüstü',
];
